import React, {
  Dispatch,
  PropsWithChildren,
  SetStateAction,
  useEffect,
  useMemo,
} from "react";
import Avo, { Platform } from "analytics/Avo";
import * as Inspector from "avo-inspector";
import mixpanel from "mixpanel-browser";
import { ExtraLoginParams, ModifiedAvoType } from "@shared/analytics/types";
import { useAvoProvider } from "@shared/analytics/hooks";
import useMedia from "utils/hooks/useMedia";
import { getAvoInitParameters } from "utils/avo";
import { IS_TELEGRAM_WEB_APP } from "common/constants";
import { useTelegramInitData } from "utils/hooks/misc";

export const AvoContext = React.createContext<{
  avo: ModifiedWebAvoType | undefined;
  setIsInitialized?: Dispatch<SetStateAction<boolean>>;
  setExtraLoginParams?: (extraParams: ExtraLoginParams) => void;
}>({
  avo: undefined,
});

const ENABLE_LOGGING = false;

const AvoProvider = ({ children }: PropsWithChildren) => {
  const userSystemDarkModeOn = useMedia(
    ["(prefers-color-scheme: dark)"],
    [true],
    false
  );
  const {
    setIsInitialized,
    modifiedAvo,
    avoDebugOptions,
    setExtraLoginParams,
  } = useAvoProvider(
    Avo,
    (updatedId: string, oldId: string) => mixpanel.alias(updatedId, oldId),
    ENABLE_LOGGING,
    userSystemDarkModeOn,
    IS_TELEGRAM_WEB_APP
  );
  const { inspectorEnv, avoEnv, mixpanelToken } = getAvoInitParameters(
    `${import.meta.env.VITE_APP_ENVIRONMENT}`
  );
  const { initData: telegramInitData } = useTelegramInitData();

  useEffect(() => {
    const inspector = new Inspector.AvoInspector({
      apiKey: "EaLGSQEReMgdOe8MGRaz",
      env: inspectorEnv,
      version: "1.0.0",
      appName: "Cornix Web",
    });
    inspector.enableLogging(ENABLE_LOGGING);
    mixpanel.init(mixpanelToken, { debug: ENABLE_LOGGING });
    Avo.initAvo(
      {
        env: avoEnv,
        inspector: inspector,
        ...avoDebugOptions,
      },
      { platform: Platform.WEBSITE },
      undefined
    );
    if (!telegramInitData) {
      setIsInitialized(true);
    }
  }, []);

  const contextValue = useMemo(
    () => ({ avo: modifiedAvo, setIsInitialized, setExtraLoginParams }),
    [modifiedAvo]
  );

  return (
    <AvoContext.Provider value={contextValue}>{children}</AvoContext.Provider>
  );
};

export default AvoProvider;

type AvoType = typeof Avo;
type ModifiedWebAvoType = ModifiedAvoType<AvoType>;
